import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS, hasEffectivePermission } from '../utils/accessControl';
import '../styles/Dashboard.css';
import '../styles/OperationsCenter.css';

const API_BASE = API_BASE_URL;

const getCurrentPeriod = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
};

const formatMonth = (year, month) => `${month.toString().padStart(2, '0')}.${year}`;

const formatCount = (value) =>
  new Intl.NumberFormat('tr-TR', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatMemory = (value) => `${Number(value || 0).toFixed(1)} MB`;

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds || 0);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return `${days}g ${hours}s`;
  }
  if (hours > 0) {
    return `${hours}s ${minutes}dk`;
  }
  return `${minutes}dk`;
};

const OperationsCenter = () => {
  const { user, permissions } = useAuth();
  const currentPeriod = useMemo(() => getCurrentPeriod(), []);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);
  const [systemStatusError, setSystemStatusError] = useState('');
  const [systemStatus, setSystemStatus] = useState({
    started_at: '',
    uptime_seconds: 0,
    host: '',
    pid: 0,
    node_version: '',
    active_requests: 0,
    total_requests: 0,
    total_errors: 0,
    slow_requests: 0,
    slow_request_threshold_ms: 0,
    memory: {
      rss_mb: 0,
      heap_used_mb: 0,
      heap_total_mb: 0,
    },
    status_breakdown: {},
    top_routes: [],
    recent_errors: [],
  });

  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacityError, setCapacityError] = useState('');
  const [capacityWeekDays, setCapacityWeekDays] = useState(7);
  const [capacityOverview, setCapacityOverview] = useState({
    summary: {
      total_people: 0,
      overloaded_people: 0,
      critical_people: 0,
      average_utilization_pct: 0,
    },
    rows: [],
    alerts: [],
    rebalancing: {
      summary: {
        impacted_sources: 0,
        impacted_targets: 0,
        total_suggestions: 0,
      },
      suggestions: [],
    },
  });

  const [earlyWarningLoading, setEarlyWarningLoading] = useState(false);
  const [earlyWarningError, setEarlyWarningError] = useState('');
  const [earlyWarningData, setEarlyWarningData] = useState({
    summary: {
      tracked_projects: 0,
      critical_projects: 0,
      high_projects: 0,
      medium_projects: 0,
      overdue_task_total: 0,
      pending_puantaj_total: 0,
    },
    rows: [],
    alerts: [],
  });

  const [subcontractorLoading, setSubcontractorLoading] = useState(false);
  const [subcontractorError, setSubcontractorError] = useState('');
  const [subcontractorNotice, setSubcontractorNotice] = useState('');
  const [subcontractorOverview, setSubcontractorOverview] = useState({
    period: currentPeriod,
    summary: {
      tracked_subcontractors: 0,
      critical_subcontractors: 0,
      high_risk_subcontractors: 0,
      average_score: 0,
      total_claimed_amount: 0,
      total_approved_amount: 0,
    },
    rows: [],
    alerts: [],
  });
  const [claimForm, setClaimForm] = useState({
    project_id: '',
    team_id: '',
    year: currentPeriod.year,
    month: currentPeriod.month,
    claimed_amount: '',
    approved_amount: '',
    planned_progress_pct: '',
    claimed_progress_pct: '',
    approved_progress_pct: '',
    retention_amount: '',
    invoice_status: 'draft',
    due_date: '',
    note: '',
  });

  const canManageClaims =
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.ACCOUNTANT].includes(Number(user?.role_id)) &&
    hasEffectivePermission(user, permissions, 'can_manage_finance');
  const canViewSystemStatus = [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER].includes(Number(user?.role_id));

  const subcontractorTeams = useMemo(() => {
    return teams.filter((team) => String(team.type || '').toLowerCase() === 'subcontractor');
  }, [teams]);

  const statusBreakdownEntries = useMemo(
    () =>
      Object.entries(systemStatus.status_breakdown || {}).sort(
        ([statusA], [statusB]) => Number(statusA) - Number(statusB)
      ),
    [systemStatus.status_breakdown]
  );

  const fetchReferenceData = useCallback(async () => {
    try {
      const [projectsResponse, teamsResponse] = await Promise.all([
        apiClient.get(`${API_BASE}/projects`),
        apiClient.get(`${API_BASE}/teams`),
      ]);
      setProjects(Array.isArray(projectsResponse.data) ? projectsResponse.data : []);
      setTeams(Array.isArray(teamsResponse.data) ? teamsResponse.data : []);
    } catch (error) {
      setProjects([]);
      setTeams([]);
    }
  }, []);

  const fetchSystemStatus = useCallback(async () => {
    if (!canViewSystemStatus) {
      return;
    }

    try {
      setSystemStatusLoading(true);
      setSystemStatusError('');
      const response = await apiClient.get(`${API_BASE}/system/runtime`);
      const data = response.data || {};
      setSystemStatus({
        started_at: data.started_at || '',
        uptime_seconds: Number(data.uptime_seconds || 0),
        host: data.host || '',
        pid: Number(data.pid || 0),
        node_version: data.node_version || '',
        active_requests: Number(data.active_requests || 0),
        total_requests: Number(data.total_requests || 0),
        total_errors: Number(data.total_errors || 0),
        slow_requests: Number(data.slow_requests || 0),
        slow_request_threshold_ms: Number(data.slow_request_threshold_ms || 0),
        memory: {
          rss_mb: Number(data.memory?.rss_mb || 0),
          heap_used_mb: Number(data.memory?.heap_used_mb || 0),
          heap_total_mb: Number(data.memory?.heap_total_mb || 0),
        },
        status_breakdown: data.status_breakdown || {},
        top_routes: Array.isArray(data.top_routes) ? data.top_routes : [],
        recent_errors: Array.isArray(data.recent_errors) ? data.recent_errors : [],
      });
    } catch (requestError) {
      setSystemStatusError(requestError.response?.data?.message || 'Sistem durumu yüklenemedi.');
    } finally {
      setSystemStatusLoading(false);
    }
  }, [canViewSystemStatus]);

  const fetchCapacityOverview = useCallback(async (weekDays = 7) => {
    try {
      setCapacityLoading(true);
      setCapacityError('');
      const response = await apiClient.get(
        `${API_BASE}/tasks/capacity/overview?week_days=${Number(weekDays) || 7}`
      );
      const data = response.data || {};
      setCapacityOverview({
        summary: {
          total_people: Number(data.summary?.total_people || 0),
          overloaded_people: Number(data.summary?.overloaded_people || 0),
          critical_people: Number(data.summary?.critical_people || 0),
          average_utilization_pct: Number(data.summary?.average_utilization_pct || 0),
        },
        rows: Array.isArray(data.rows) ? data.rows : [],
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
        rebalancing: {
          summary: {
            impacted_sources: Number(data.rebalancing?.summary?.impacted_sources || 0),
            impacted_targets: Number(data.rebalancing?.summary?.impacted_targets || 0),
            total_suggestions: Number(data.rebalancing?.summary?.total_suggestions || 0),
          },
          suggestions: Array.isArray(data.rebalancing?.suggestions) ? data.rebalancing.suggestions : [],
        },
      });
    } catch (requestError) {
      setCapacityError(requestError.response?.data?.message || 'Kapasite verileri yüklenemedi.');
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  const fetchEarlyWarningCenter = useCallback(async () => {
    try {
      setEarlyWarningLoading(true);
      setEarlyWarningError('');
      const response = await apiClient.get(`${API_BASE}/projects/early-warning?limit=20`);
      const data = response.data || {};
      setEarlyWarningData({
        summary: {
          tracked_projects: Number(data.summary?.tracked_projects || 0),
          critical_projects: Number(data.summary?.critical_projects || 0),
          high_projects: Number(data.summary?.high_projects || 0),
          medium_projects: Number(data.summary?.medium_projects || 0),
          overdue_task_total: Number(data.summary?.overdue_task_total || 0),
          pending_puantaj_total: Number(data.summary?.pending_puantaj_total || 0),
        },
        rows: Array.isArray(data.rows) ? data.rows : [],
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
      });
    } catch (requestError) {
      setEarlyWarningError(requestError.response?.data?.message || 'Erken uyarı verileri yüklenemedi.');
    } finally {
      setEarlyWarningLoading(false);
    }
  }, []);

  const fetchSubcontractorOverview = useCallback(async () => {
    try {
      setSubcontractorLoading(true);
      setSubcontractorError('');
      const query = new URLSearchParams({
        year: String(claimForm.year),
        month: String(claimForm.month),
      });
      const response = await apiClient.get(`${API_BASE}/subcontractor-performance/overview?${query.toString()}`);
      const data = response.data || {};
      setSubcontractorOverview({
        period: data.period || { year: claimForm.year, month: claimForm.month },
        summary: {
          tracked_subcontractors: Number(data.summary?.tracked_subcontractors || 0),
          critical_subcontractors: Number(data.summary?.critical_subcontractors || 0),
          high_risk_subcontractors: Number(data.summary?.high_risk_subcontractors || 0),
          average_score: Number(data.summary?.average_score || 0),
          total_claimed_amount: Number(data.summary?.total_claimed_amount || 0),
          total_approved_amount: Number(data.summary?.total_approved_amount || 0),
        },
        rows: Array.isArray(data.rows) ? data.rows : [],
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
      });
    } catch (requestError) {
      setSubcontractorError(requestError.response?.data?.message || 'Taşeron performans verileri yüklenemedi.');
      setSubcontractorOverview((prev) => ({ ...prev, rows: [], alerts: [] }));
    } finally {
      setSubcontractorLoading(false);
    }
  }, [claimForm.month, claimForm.year]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchSystemStatus();
  }, [fetchSystemStatus]);

  useEffect(() => {
    fetchCapacityOverview(capacityWeekDays);
  }, [capacityWeekDays, fetchCapacityOverview]);

  useEffect(() => {
    fetchEarlyWarningCenter();
  }, [fetchEarlyWarningCenter]);

  useEffect(() => {
    fetchSubcontractorOverview();
  }, [fetchSubcontractorOverview]);

  const handleClaimFormChange = (field, value) => {
    setClaimForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveClaim = async (event) => {
    event.preventDefault();
    if (!canManageClaims) return;
    setSubcontractorNotice('');
    try {
      await apiClient.post(`${API_BASE}/subcontractor-performance/claims`, claimForm);
      setSubcontractorNotice('Hakediş verisi kaydedildi. Performans tablosu güncellendi.');
      fetchSubcontractorOverview();
    } catch (error) {
      setSubcontractorError(error.response?.data?.message || 'Hakediş kaydı kaydedilemedi.');
    }
  };

  return (
    <div className="operations-center-page">
      <div className="operations-hero">
        <h1>Operasyon Merkezi</h1>
        <p>Kapasite dengeleme, erken uyarı, hakediş ve taşeron performansını tek ekranda yönetin.</p>
      </div>

      {canViewSystemStatus && (
        <div className="system-status-section">
          <div className="section-header">
            <div>
              <h2>Sistem Durumu</h2>
              <p className="subcontractor-subtitle">
                Canlı istek yükünü, hata yoğunluğunu ve bellek kullanımını operasyon ekranından izleyin.
              </p>
            </div>
            <div className="capacity-head-actions">
              <button
                type="button"
                className="view-all system-refresh-btn"
                onClick={fetchSystemStatus}
                disabled={systemStatusLoading}
              >
                {systemStatusLoading ? 'Yükleniyor...' : 'Yenile'}
              </button>
            </div>
          </div>

          {systemStatusError && <div className="error-alert">{systemStatusError}</div>}

          {systemStatusLoading ? (
            <div className="metrics-loading">Sistem durumu yükleniyor...</div>
          ) : (
            <>
              <div className="system-status-grid">
                <div className="capacity-summary-card">
                  <span>Çalışma Süresi</span>
                  <strong>{formatDuration(systemStatus.uptime_seconds)}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Toplam İstek</span>
                  <strong>{formatCount(systemStatus.total_requests)}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Aktif İstek</span>
                  <strong>{formatCount(systemStatus.active_requests)}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Toplam Hata</span>
                  <strong>{formatCount(systemStatus.total_errors)}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Yavaş İstek</span>
                  <strong>{formatCount(systemStatus.slow_requests)}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Heap Kullanımı</span>
                  <strong>{formatMemory(systemStatus.memory.heap_used_mb)}</strong>
                </div>
              </div>

              <div className="system-status-meta">
                <span>Host: {systemStatus.host || '-'}</span>
                <span>PID: {systemStatus.pid || '-'}</span>
                <span>Node: {systemStatus.node_version || '-'}</span>
                <span>Başlangıç: {systemStatus.started_at ? new Date(systemStatus.started_at).toLocaleString('tr-TR') : '-'}</span>
                <span>RSS: {formatMemory(systemStatus.memory.rss_mb)}</span>
                <span>Heap Tavan: {formatMemory(systemStatus.memory.heap_total_mb)}</span>
              </div>

              {statusBreakdownEntries.length > 0 && (
                <div className="system-status-breakdown">
                  {statusBreakdownEntries.map(([statusCode, count]) => (
                    <div key={`status-${statusCode}`} className="system-status-chip">
                      <span>{statusCode}</span>
                      <strong>{formatCount(count)}</strong>
                    </div>
                  ))}
                </div>
              )}

              <div className="system-status-panels">
                <div className="system-status-card">
                  <div className="system-status-card-head">
                    <h3>Yoğun Route'lar</h3>
                    <span>Yavaş eşik: {formatCount(systemStatus.slow_request_threshold_ms)} ms</span>
                  </div>
                  {systemStatus.top_routes.length === 0 ? (
                    <p className="capacity-rebalance-empty">Henüz route verisi oluşmadı.</p>
                  ) : (
                    <div className="system-route-table">
                      <div className="system-route-row system-route-head">
                        <span>Route</span>
                        <span>İstek</span>
                        <span>Ort. Süre</span>
                        <span>Yavaş</span>
                        <span>500+</span>
                      </div>
                      {systemStatus.top_routes.map((row) => (
                        <div
                          key={`system-route-${row.route}`}
                          className="system-route-row"
                        >
                          <span>{row.route}</span>
                          <span>{formatCount(row.count)}</span>
                          <span>{formatCount(row.avgDurationMs)} ms</span>
                          <span>{formatCount(row.slowCount)}</span>
                          <span>{formatCount(row.errorCount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="system-status-card">
                  <div className="system-status-card-head">
                    <h3>Son Hatalar</h3>
                    <span>En yeni 20 kayıt</span>
                  </div>
                  {systemStatus.recent_errors.length === 0 ? (
                    <p className="capacity-rebalance-empty">Kayıtlı son hata yok.</p>
                  ) : (
                    <div className="system-error-list">
                      {systemStatus.recent_errors.map((errorItem) => (
                        <div
                          key={`${errorItem.requestId || 'error'}-${errorItem.at}`}
                          className="system-error-item"
                        >
                          <div className="system-error-head">
                            <strong>{errorItem.method} {errorItem.path}</strong>
                            <span>{errorItem.at ? new Date(errorItem.at).toLocaleString('tr-TR') : '-'}</span>
                          </div>
                          <div className="system-error-message">{errorItem.message}</div>
                          <div className="system-error-meta">
                            Request ID: {errorItem.requestId || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="capacity-section">
        <div className="section-header">
          <h2>Kaynak Kapasite Merkezi</h2>
          <div className="capacity-head-actions">
            <select
              className="capacity-range-select"
              value={capacityWeekDays}
              onChange={(event) => setCapacityWeekDays(Number(event.target.value))}
            >
              <option value={7}>7 Gün</option>
              <option value={14}>14 Gün</option>
              <option value={21}>21 Gün</option>
            </select>
            <Link to="/tasks" className="view-all">Görevlere Git</Link>
          </div>
        </div>

        {capacityError && <div className="error-alert">{capacityError}</div>}

        {capacityLoading ? (
          <div className="metrics-loading">Kapasite verileri yükleniyor...</div>
        ) : (
          <>
            <div className="capacity-summary-grid">
              <div className="capacity-summary-card"><span>Takip Edilen Personel</span><strong>{capacityOverview.summary.total_people}</strong></div>
              <div className="capacity-summary-card"><span>Aşırı Yüklenme</span><strong>{capacityOverview.summary.overloaded_people}</strong></div>
              <div className="capacity-summary-card"><span>Kritik Risk</span><strong>{capacityOverview.summary.critical_people}</strong></div>
              <div className="capacity-summary-card"><span>Ortalama Doluluk</span><strong>%{capacityOverview.summary.average_utilization_pct}</strong></div>
            </div>

            {capacityOverview.alerts.length > 0 && (
              <div className="capacity-alert-list">
                {capacityOverview.alerts.map((alert) => (
                  <div key={`${alert.user_id}-${alert.level}`} className={`capacity-alert-item ${alert.level}`}>
                    <div className="capacity-alert-message">{alert.message}</div>
                    <div className="capacity-alert-suggestion">{alert.suggestion}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="capacity-rebalance-panel">
              <div className="capacity-rebalance-head">
                <h3>Dengeleme Simülasyonu</h3>
                <span>Kaynak: {capacityOverview.rebalancing.summary.impacted_sources} | Hedef: {capacityOverview.rebalancing.summary.impacted_targets}</span>
              </div>
              {capacityOverview.rebalancing.suggestions.length === 0 ? (
                <p className="capacity-rebalance-empty">Simülasyon için otomatik yeniden dağıtım önerisi oluşmadı.</p>
              ) : (
                <div className="capacity-rebalance-list">
                  {capacityOverview.rebalancing.suggestions.map((item, index) => (
                    <div className="capacity-rebalance-item" key={`rebalance-${index}-${item.from_user_id}-${item.to_user_id}`}>
                      <div className="capacity-rebalance-route"><strong>{item.from_username}</strong><span>-&gt;</span><strong>{item.to_username}</strong></div>
                      <div className="capacity-rebalance-meta">
                        <span>Kaynak %{item.projected_from_utilization_pct} | Hedef %{item.projected_to_utilization_pct}</span>
                        <span>{item.suggested_task_title ? `Örnek Görev: ${item.suggested_task_title}` : 'Görev: uygun açık işlerden seçilmeli'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="capacity-table-wrap">
              <div className="capacity-table-row capacity-head-row">
                <span>Kişi</span><span>Rol/Unvan</span><span>Aktif</span><span>Son Tarihi Yaklaşan</span><span>Geciken</span><span>Doluluk</span><span>Risk</span>
              </div>
              {capacityOverview.rows.map((row) => (
                <div className="capacity-table-row" key={`capacity-${row.user_id}`}>
                  <span>{row.username}</span><span>{row.job_title || '-'}</span><span>{row.active_tasks}</span><span>{row.due_window_tasks}</span><span>{row.overdue_tasks}</span><span>%{row.utilization_pct}</span><span className={`capacity-risk-chip ${row.risk_level}`}>{row.risk_level}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="early-warning-section">
        <div className="section-header">
          <h2>Yönetici Erken Uyarı Merkezi</h2>
          <Link to="/projects" className="view-all">Projelere Git</Link>
        </div>

        {earlyWarningError && <div className="error-alert">{earlyWarningError}</div>}

        {earlyWarningLoading ? (
          <div className="metrics-loading">Erken uyarı verileri yükleniyor...</div>
        ) : (
          <>
            <div className="warning-summary-grid">
              <div className="warning-summary-card"><span>İzlenen Proje</span><strong>{earlyWarningData.summary.tracked_projects}</strong></div>
              <div className="warning-summary-card"><span>Kritik</span><strong>{earlyWarningData.summary.critical_projects}</strong></div>
              <div className="warning-summary-card"><span>Yüksek Risk</span><strong>{earlyWarningData.summary.high_projects}</strong></div>
              <div className="warning-summary-card"><span>Geciken Görev</span><strong>{earlyWarningData.summary.overdue_task_total}</strong></div>
            </div>

            {earlyWarningData.alerts.length > 0 && (
              <div className="warning-alert-list">
                {earlyWarningData.alerts.map((alert) => (
                  <div key={`warning-alert-${alert.project_id}`} className={`warning-alert-item ${alert.level}`}>
                    <div className="warning-alert-title">{alert.message}</div>
                    {Array.isArray(alert.suggestions) && alert.suggestions.length > 0 && (
                      <div className="warning-alert-suggestions">
                        {alert.suggestions.slice(0, 3).map((suggestion, idx) => (
                          <span key={`warning-suggestion-${alert.project_id}-${idx}`}>{suggestion}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="warning-table-wrap">
              <div className="warning-table-row warning-table-head">
                <span>Proje</span><span>Risk</span><span>Skor</span><span>Geciken</span><span>Puantaj</span><span>Rapor</span>
              </div>
              {earlyWarningData.rows.map((row) => (
                <div className="warning-table-row" key={`warning-row-${row.project_id}`}>
                  <span><Link to={`/projects/${row.project_id}`}>{row.project_name}</Link></span>
                  <span className={`warning-risk-chip ${row.risk_level}`}>{row.risk_level}</span>
                  <span>{row.risk_score}</span>
                  <span>{row.overdue_tasks}</span>
                  <span>{row.pending_puantaj}</span>
                  <span>{row.has_today_report ? 'Tamam' : 'Eksik'}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="subcontractor-section">
        <div className="section-header">
          <div>
            <h2>Hakediş ve Taşeron Performansı</h2>
            <p className="subcontractor-subtitle">Puantaj, kalite, teslim ve fatura akışını tek KPI setinde izleyin.</p>
          </div>
          <div className="capacity-head-actions">
            <select className="capacity-range-select" value={claimForm.month} onChange={(event) => handleClaimFormChange('month', Number(event.target.value))}>
              {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => <option key={month} value={month}>{month}. Ay</option>)}
            </select>
            <select className="capacity-range-select" value={claimForm.year} onChange={(event) => handleClaimFormChange('year', Number(event.target.value))}>
              {[currentPeriod.year - 1, currentPeriod.year, currentPeriod.year + 1].map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
        </div>

        {subcontractorError && <div className="error-alert">{subcontractorError}</div>}
        {subcontractorNotice && <div className="success-alert">{subcontractorNotice}</div>}

        <div className="subcontractor-summary-grid">
          <div className="capacity-summary-card"><span>İzlenen Taşeron</span><strong>{subcontractorOverview.summary.tracked_subcontractors}</strong></div>
          <div className="capacity-summary-card"><span>Kritik Risk</span><strong>{subcontractorOverview.summary.critical_subcontractors}</strong></div>
          <div className="capacity-summary-card"><span>Ortalama Skor</span><strong>{subcontractorOverview.summary.average_score}</strong></div>
          <div className="capacity-summary-card"><span>Toplam Hakediş</span><strong>{formatCurrency(subcontractorOverview.summary.total_claimed_amount)}</strong></div>
        </div>

        {subcontractorOverview.alerts.length > 0 && (
          <div className="subcontractor-alert-list">
            {subcontractorOverview.alerts.map((alert) => (
              <div key={`${alert.claim_id}-${alert.team_id}`} className={`capacity-alert-item ${alert.level}`}>
                <div className="capacity-alert-message">{alert.message}</div>
                <div className="capacity-alert-suggestion">{Array.isArray(alert.reasons) ? alert.reasons.join(' | ') : ''}</div>
              </div>
            ))}
          </div>
        )}

        {canManageClaims && (
          <form className="subcontractor-claim-form" onSubmit={handleSaveClaim}>
            <div className="subcontractor-form-grid">
              <select className="capacity-range-select" value={claimForm.project_id} onChange={(event) => handleClaimFormChange('project_id', event.target.value)} required>
                <option value="">Proje seçin</option>
                {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              </select>
              <select className="capacity-range-select" value={claimForm.team_id} onChange={(event) => handleClaimFormChange('team_id', event.target.value)} required>
                <option value="">Taşeron seçin</option>
                {subcontractorTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
              <input className="capacity-range-select" type="number" min="0" step="0.01" placeholder="Talep edilen tutar" value={claimForm.claimed_amount} onChange={(event) => handleClaimFormChange('claimed_amount', event.target.value)} />
              <input className="capacity-range-select" type="number" min="0" step="0.01" placeholder="Onaylanan tutar" value={claimForm.approved_amount} onChange={(event) => handleClaimFormChange('approved_amount', event.target.value)} />
              <input className="capacity-range-select" type="number" min="0" max="100" step="0.1" placeholder="Plan %" value={claimForm.planned_progress_pct} onChange={(event) => handleClaimFormChange('planned_progress_pct', event.target.value)} />
              <input className="capacity-range-select" type="number" min="0" max="100" step="0.1" placeholder="Onaylanan ilerleme %" value={claimForm.approved_progress_pct} onChange={(event) => handleClaimFormChange('approved_progress_pct', event.target.value)} />
              <select className="capacity-range-select" value={claimForm.invoice_status} onChange={(event) => handleClaimFormChange('invoice_status', event.target.value)}>
                <option value="draft">Taslak</option>
                <option value="submitted">Sunuldu</option>
                <option value="approved">Onaylandı</option>
                <option value="paid">Ödendi</option>
                <option value="disputed">İhtilaflı</option>
              </select>
              <button className="view-all subcontractor-save-btn" type="submit">Hakediş Kaydet</button>
            </div>
          </form>
        )}

        {subcontractorLoading ? (
          <div className="metrics-loading">Taşeron performansı yükleniyor...</div>
        ) : subcontractorOverview.rows.length === 0 ? (
          <div className="capacity-rebalance-empty">{formatMonth(subcontractorOverview.period.year, subcontractorOverview.period.month)} dönemi için hakediş verisi yok.</div>
        ) : (
          <div className="subcontractor-table-wrap">
            <div className="subcontractor-table-row subcontractor-table-head">
              <span>Taşeron</span><span>Proje</span><span>Skor</span><span>Risk</span><span>Hakediş</span><span>Onay</span><span>Kalite</span><span>Teslim</span>
            </div>
            {subcontractorOverview.rows.map((row) => (
              <div className="subcontractor-table-row" key={`subcontractor-${row.claim_id}`}>
                <span>{row.team_name}</span>
                <span>{row.project_name}</span>
                <span>{row.overall_score}</span>
                <span className={`warning-risk-chip ${row.risk_level}`}>{row.risk_level}</span>
                <span>{formatCurrency(row.claimed_amount)}</span>
                <span>{row.invoice_status}</span>
                <span>{row.quality_score}</span>
                <span>{row.delivery_score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsCenter;
